import { transform as olTransform } from 'ol/proj';
import * as olSphere from 'ol/sphere';
import { Polygon } from 'ol/geom';
import { Feature } from 'ol';
import { geoJsonExport } from './geoJsonExport';
import { segmentApi, segmentTextApi, segmentImageApi, presignedUrlApi, PresignedUrlResponse } from './volume/utils/api';
import GeoJSON from 'ol/format/GeoJSON';
import OlMap from 'ol/Map';
import { getHighResMapCanvas, freezeMap, unfreezeMap } from '@openlayers/ol-utils';
import { observable, action, makeObservable } from 'mobx';
import { AreaTool } from './tool-area';
import { VolumeTool } from './volume/tool-volumes';
import { addLoadingIcon, removeLoadingIcon } from './tool-labels';

class ToolsDebugStore {
    @observable enabled = false;
    /* 
    In browser terminal paste: toolsdebug.enable() to enable debug logging
    In browser terminal paste: toolsdebug.disable() to disable debug logging
    */

    constructor() {
        makeObservable(this);
    }

    @action
    enable() {
        this.enabled = true;
        console.log('Tools debug: ON');
    }

    @action
    disable() {
        this.enabled = false;
        console.log('Tools debug: OFF');
    }
}
export const ToolsDebug = new ToolsDebugStore();
if (typeof window !== 'undefined') {
    (window as any).toolsdebug = ToolsDebug;
}

export class AiMeasurementsStore {
    @observable groups: { features: Feature[]; text: string; id: number }[] = [];
    private idCounter = 0;

    constructor() {
        makeObservable(this);
    }

    @action
    add(features: Feature[], text: string = '') {
        this.groups.push({ features, text, id: this.idCounter++ });
    }

    @action
    removeGroup(group: { features: Feature[]; text: string; id: number }) {
        const index = this.groups.indexOf(group);
        if (index > -1) {
            this.groups.splice(index, 1);
        }
    }

    @action
    clear() {
        this.groups = [];
    }
}
export const AiMeasurements = new AiMeasurementsStore();

export async function searchToGeoJson(tool: AreaTool | VolumeTool, text: string): Promise<void> {
    /** 
    Get geojson features based on text search
    */
    const { mapS3Key, featureS3Key, extent } = await uploadDataForAI(tool);

    const outGeoJson: Blob = await segmentTextApi(mapS3Key, extent, text);

    const features = await geoJsonToMap({ tool, geoJson: outGeoJson, text });

    AiMeasurements.add(features, `"${text}"`);
}

export async function boundsToGeoJson(tool: AreaTool | VolumeTool, olFeature: Feature): Promise<void> {
    /** 
    Get geojson features based on rough bounds of feature
    */

    // Use feature for zoom extends instead of users map extent
    let zoomExtent = olFeature.getGeometry().getExtent();
    const expand = 5; // Add 5 meters buffer to extent
    zoomExtent = [zoomExtent[0] - expand, zoomExtent[1] - expand, zoomExtent[2] + expand, zoomExtent[3] + expand];

    const { mapS3Key, featureS3Key, extent } = await uploadDataForAI(tool, olFeature, zoomExtent);

    const outGeoJson: Blob = await segmentApi(mapS3Key, featureS3Key, extent);

    await geoJsonToMap({ tool, geoJson: outGeoJson, feature: olFeature, areaThreshold: 1 });
}

export async function findSimilar(tool: AreaTool | VolumeTool, olFeature: Feature): Promise<void> {
    /** 
    Find similar features using AI
    Function handles removing loading icon and original feature when appropriate
    */
    const { mapS3Key, featureS3Key, extent } = await uploadDataForAI(tool, olFeature);

    const outGeoJson: Blob = await segmentImageApi(mapS3Key, featureS3Key, extent);

    const features = await geoJsonToMap({ tool, geoJson: outGeoJson, feature: olFeature });

    AiMeasurements.add(features, 'Similarity search');
}

export const getCenter = (coordinates: number[]): number[] => {
    const n = coordinates.length;
    const center = [];

    coordinates.forEach(c => {
        center[0] += c[0];
        center[1] += c[1];
    });

    center[0] = center[0] / n;
    center[1] = center[1] / n;

    return center;
};

export const getArea = (localCoordinates: number[][]) => {
    /**
    Assumes first and last coordinate of the coordinates polygon are equal
    */
    let left = 0;
    let right = 0;
    let area = 0;

    for (let i = 0, n = localCoordinates.length - 1; i < n; i++) {
        left = left + localCoordinates[i][0] * localCoordinates[i + 1][1];
        right = right + localCoordinates[i + 1][0] * localCoordinates[i][1];
    }

    area = (left - right) / 2;

    return Math.abs(area);
};

export const getLocalCoordinatesInCcwOrder = (polygon, sourceProj) => {
    /**
    Assumes GeometryType.POLYGON with only the outer polygon.
    */
    const coordinates = polygon.getCoordinates()[0];
    const list = [];

    for (let i = 0, n = coordinates.length; i < n; i++) {
        list[i] = olTransform(coordinates[i], sourceProj, kuura.getProjectProj4Code());
    }

    const center = getCenter(list);
    const fullCircle = 2 * Math.PI;

    list.sort((a, b) => {
        const a1 = Math.atan2(a[0] - center[0], a[1] - center[1]) + fullCircle;
        const a2 = Math.atan2(b[0] - center[0], b[1] - center[1]) + fullCircle;
        return a2 - a1;
    });

    return list;
};

export const calcArea = (polygon, sourceProj) => {
    let area: number;
    let prefix: string = '';
    if (kuura.getProjectProj4Code() === 'EPSG:4326') {
        area = Math.abs(olSphere.getArea(polygon));
        prefix = '~';
    } else {
        const ccwLocalCoordinates = getLocalCoordinatesInCcwOrder(polygon, sourceProj);
        area = getArea(ccwLocalCoordinates);
    }
    return { area, prefix };
};

async function addFeatures(tool: AreaTool | VolumeTool, features: Feature[], areaThreshold: number = 0, text?: string): Promise<void> {
    // Only show labels if less than 20 features that are larger than the area threshold
    const featureCount = features.filter(f => calcArea(f.getGeometry(), tool.sourceProj).area > areaThreshold).length;
    const showLabel = featureCount <= 20;

    features.forEach(feature => {
        const geom = feature.getGeometry() as Polygon;
        if (calcArea(geom, tool.sourceProj).area > areaThreshold) {
            tool.addFeature(feature, showLabel, text);
        }
    });
}

interface dataUploadResponse {
    mapS3Key: string;
    featureS3Key: string | null;
    extent: number[];
}

async function uploadDataForAI(tool: AreaTool | VolumeTool, feature?: Feature, zoomExtent?: number[]): Promise<dataUploadResponse> {
    // At very first add loading icon
    if (feature) {
        await addLoadingIcon(tool.mapWrapper, feature);
    }

    // Freeze map while loading high res image
    const placeholder = freezeMap(tool.map);

    // Delete measurement so it does not affect crop result
    if (feature) {
        tool.removeMeasurement(feature);
    }

    // Start exporting map as WebP
    const webpPromise: Promise<MapExport> = getWebP(tool.map, zoomExtent);

    let featureS3Key: string | null = null;
    if (feature) {
        // Convert feature into GeoJSON and upload to s3
        const inGeoJsonBuffer: ArrayBuffer = await geoJsonExport.getArrayBuffer([feature]);
        const featureUrlResponse: PresignedUrlResponse = await presignedUrlApi('.json');

        if (ToolsDebug.enabled) {
            await exportDebugFile(new Blob([inGeoJsonBuffer]), `debug-feature-${Date.now()}.json`);
        }

        await fetch(featureUrlResponse.presigned_url, {
            method: 'PUT',
            body: inGeoJsonBuffer,
        });
        featureS3Key = featureUrlResponse.s3_key;
    }

    // Get presigned URL for Map image
    const presignedUrlResponse: PresignedUrlResponse = await presignedUrlApi('.webp');

    // Ensure WebP image is ready
    const webpImage: MapExport = await webpPromise;

    // Unfreeze map before uploading to S3
    if (feature) {
        tool.addFeature(feature);
    }
    unfreezeMap(placeholder);

    console.log(`WebP size: ${(webpImage.arrayBuffer.byteLength / 1024 / 1024).toFixed(2)} MB`);
    const start = performance.now();

    // Upload WebP image to S3
    await fetch(presignedUrlResponse.presigned_url, {
        method: 'PUT',
        body: webpImage.arrayBuffer,
    });

    const end = performance.now();
    console.log(`Upload took ${(end - start).toFixed(2)} ms`);

    const response: dataUploadResponse = { mapS3Key: presignedUrlResponse.s3_key, featureS3Key: featureS3Key, extent: webpImage.extent };
    return response;
}

interface IGeoJsonToMap {
    tool: AreaTool | VolumeTool;
    geoJson: Blob;
    feature?: Feature;
    areaThreshold?: number;
    text?: string;
}

async function geoJsonToMap({ tool, geoJson, feature, areaThreshold = 0, text }: IGeoJsonToMap): Promise<Feature[]> {
    /*
    Add GeoJSON to users mapview
    */
    const geoJsonText = await geoJson.text();
    const features = new GeoJSON().readFeatures(geoJsonText);

    if (features.length > 0 && feature) {
        tool.removeMeasurement(feature);
    }

    await addFeatures(tool, features, areaThreshold, text);

    // Finally remove loading icon
    if (feature) {
        removeLoadingIcon(tool.mapWrapper, feature);
    }

    return features;
}

async function calcScale(zoom: number): Promise<number> {
    let scale: number;
    if (zoom >= 20) {
        scale = 1;
    } else if (zoom >= 19) {
        scale = 1.5;
    } else {
        scale = 2;
    }
    return scale;
}

interface MapExport {
    arrayBuffer: ArrayBuffer;
    extent: number[];
}

async function getWebP(map: OlMap, zoomExtent?: number[]): Promise<MapExport> {
    // Convert feature into GeoJSON
    const view = map.getView();
    const originalExtent: number[] = view.calculateExtent(map.getSize());

    // TODO use BBOX typings
    let newExtent: number[] = [];
    if (zoomExtent) {
        view.fit(zoomExtent);
        newExtent = view.calculateExtent(map.getSize());
    } else {
        newExtent = originalExtent;
    }

    // Calculate map extent
    console.log('zoomExtent', map.getSize());
    console.log('newExtent', newExtent);

    // Calculate GSD for geotiff
    const zoom = view.getZoom();
    const scale = await calcScale(zoom);

    // Get canvas of the map image
    const canvas: HTMLCanvasElement = await getHighResMapCanvas(map, false, scale);
    const blob = await new Promise<Blob>(resolve => canvas.toBlob(resolve, 'image/webp', 0.8));
    const arrayBuffer = await blob.arrayBuffer();

    if (ToolsDebug.enabled) {
        await exportDebugFile(blob, `debug-map-${Date.now()}.webp`);
    }
    if (zoomExtent) {
        view.fit(originalExtent);
    }

    return { arrayBuffer, extent: newExtent };
}

export async function exportDebugFile(blob: Blob, filename: string): Promise<void> {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
